import { useState, type ChangeEvent, type FormEvent } from "react";
import { FileUp, PlusCircle, ReceiptText } from "lucide-react";
import type { Currency, NewSale, SaleStatus } from "../domain/types";
import {
  Button,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusChip,
} from "../components/UI";
import { dateTime, money, shortDate } from "../lib/format";
import {
  parseAffiliateReport,
  reportFieldLabels,
  type ParsedReport,
  type ReportField,
  type ReportMapping,
} from "../lib/importReport";
import { PlatformBadge } from "../components/PlatformBadge";
import { useWeSell } from "../state/WeSellProvider";

async function fingerprint(file: File): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(hash))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

export function Sales() {
  const { data, saving, addSale, addImportedSales } = useWeSell();
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [platformId, setPlatformId] = useState("");
  const [productId, setProductId] = useState("");
  const [promotionId, setPromotionId] = useState("");
  const [externalId, setExternalId] = useState("");
  const [gross, setGross] = useState("");
  const [commission, setCommission] = useState("");
  const [currency, setCurrency] = useState<Currency>("BRL");
  const [status, setStatus] = useState<SaleStatus>("approved");
  const [report, setReport] = useState<ParsedReport | null>(null);
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [reportName, setReportName] = useState("");
  const [importError, setImportError] = useState("");
  if (!data) return null;

  const selectedPlatform = data.platforms.find(
    (platform) => platform.id === platformId,
  );
  const availablePromotions = data.promotions.filter(
    (item) => item.platformId === platformId,
  );

  const openManual = () => {
    setPlatformId(data.platforms[0]?.id ?? "");
    setProductId("");
    setPromotionId("");
    setCurrency("BRL");
    setManualOpen(true);
  };
  const openImport = () => {
    setPlatformId(data.platforms[0]?.id ?? "");
    setReport(null);
    setReportFile(null);
    setReportName("");
    setImportError("");
    setImportOpen(true);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const promotion = data.promotions.find((item) => item.id === promotionId);
    const input: NewSale = {
      platformId,
      productId: promotion?.productId ?? (productId || null),
      promotionId: promotionId || null,
      externalId: externalId || `MANUAL-${Date.now()}`,
      orderDate: new Date().toISOString(),
      grossAmount: Number(gross.replace(",", ".")) || 0,
      commissionAmount: Number(commission.replace(",", ".")) || 0,
      currency,
      status,
      source: "manual",
    };
    await addSale(input);
    setManualOpen(false);
  };

  const parseFile = async (
    file: File,
    mapping: Partial<ReportMapping> = {},
  ) => {
    if (!selectedPlatform) return;
    setImportError("");
    try {
      setReport(
        await parseAffiliateReport(
          file,
          selectedPlatform,
          data.products,
          data.promotions,
          mapping,
        ),
      );
    } catch (cause) {
      setImportError(
        cause instanceof Error ? cause.message : "Arquivo inválido.",
      );
    }
  };
  const fileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setReportFile(file);
    setReportName(file.name);
    await parseFile(file);
  };
  const remap = async (field: ReportField, header: string) => {
    if (!reportFile || !report) return;
    await parseFile(reportFile, {
      ...report.mapping,
      [field]: header || null,
    });
  };
  const confirmImport = async () => {
    if (!report || !reportFile) return;
    await addImportedSales({
      platformId,
      fileName: reportFile.name,
      fileFingerprint: await fingerprint(reportFile),
      totalRows: report.totalRows,
      warnings: report.warnings,
      sales: report.sales,
    });
    setImportOpen(false);
  };

  return (
    <>
      <PageHeader
        eyebrow="Receita"
        title="Vendas e comissões"
        description="Importe relatórios oficiais ou registre uma venda manualmente."
        action={
          <div className="button-group">
            <Button variant="secondary" onClick={openImport}>
              <FileUp size={18} /> Importar relatório
            </Button>
            <Button onClick={openManual}>
              <PlusCircle size={18} /> Nova venda
            </Button>
          </div>
        }
      />
      {data.sales.length ? (
        <div className="panel responsive-table">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Pedido</th>
                <th>Produto e link</th>
                <th>Plataforma</th>
                <th>Status</th>
                <th>Venda</th>
                <th>Comissão</th>
              </tr>
            </thead>
            <tbody>
              {data.sales.map((sale) => {
                const product = data.products.find(
                  (item) => item.id === sale.productId,
                );
                const promotion = data.promotions.find(
                  (item) => item.id === sale.promotionId,
                );
                const platform = data.platforms.find(
                  (item) => item.id === sale.platformId,
                );
                return (
                  <tr key={sale.id}>
                    <td>{shortDate(sale.orderDate)}</td>
                    <td>{sale.externalId}</td>
                    <td>
                      <strong>{product?.name ?? "Não vinculado"}</strong>
                      <small>
                        {promotion?.trackingId || "Sem link atribuído"}
                      </small>
                    </td>
                    <td>
                      {platform ? (
                        <PlatformBadge platform={platform} compact />
                      ) : (
                        "Não encontrada"
                      )}
                    </td>
                    <td>
                      <StatusChip
                        status={
                          sale.status === "approved"
                            ? "ok"
                            : sale.status === "reversed"
                              ? "error"
                              : "warning"
                        }
                        label={
                          sale.status === "approved"
                            ? "Aprovada"
                            : sale.status === "reversed"
                              ? "Estornada"
                              : "Pendente"
                        }
                      />
                    </td>
                    <td className="number-cell">
                      {money(sale.grossAmount, sale.currency)}
                    </td>
                    <td className="number-cell">
                      <strong>
                        {money(sale.commissionAmount, sale.currency)}
                      </strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<ReceiptText size={28} />}
          title="Nenhuma venda ainda"
          description="Quando você importar o primeiro relatório, suas comissões aparecerão aqui."
          action={<Button onClick={openImport}>Importar relatório</Button>}
        />
      )}

      {data.imports.length ? (
        <section className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Auditoria</p>
              <h2>Últimas importações</h2>
            </div>
          </header>
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Arquivo</th>
                  <th>Plataforma</th>
                  <th>Aceitas</th>
                  <th>Ignoradas</th>
                </tr>
              </thead>
              <tbody>
                {data.imports.slice(0, 8).map((batch) => {
                  const platform = data.platforms.find(
                    (item) => item.id === batch.platformId,
                  );
                  return (
                    <tr key={batch.id}>
                      <td>{dateTime(batch.createdAt)}</td>
                      <td>{batch.fileName}</td>
                      <td>{platform?.name ?? "Não encontrada"}</td>
                      <td>{batch.acceptedRows}</td>
                      <td>{batch.rejectedRows}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <Modal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        title="Registrar venda"
        footer={
          <>
            <Button variant="secondary" onClick={() => setManualOpen(false)}>
              Cancelar
            </Button>
            <Button form="sale-form" type="submit" loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <form id="sale-form" className="form-grid" onSubmit={submit}>
          <Field label="Plataforma">
            <select
              value={platformId}
              onChange={(event) => {
                setPlatformId(event.target.value);
                setProductId("");
                setPromotionId("");
              }}
            >
              {data.platforms.map((platform) => (
                <option value={platform.id} key={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Link de divulgação"
            helper="Opcional, mas necessário para medir qual conteúdo vendeu."
          >
            <select
              value={promotionId}
              onChange={(event) => {
                const promotion = data.promotions.find(
                  (item) => item.id === event.target.value,
                );
                setPromotionId(event.target.value);
                if (promotion) setProductId(promotion.productId);
              }}
            >
              <option value="">Sem link atribuído</option>
              {availablePromotions.map((promotion) => {
                const product = data.products.find(
                  (item) => item.id === promotion.productId,
                );
                return (
                  <option value={promotion.id} key={promotion.id}>
                    {product?.shortCode ?? "Item"} ·{" "}
                    {promotion.trackingId || "sem tracking"}
                  </option>
                );
              })}
            </select>
          </Field>
          <Field label="Produto">
            <select
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              disabled={Boolean(promotionId)}
            >
              <option value="">Sem vínculo</option>
              {data.products
                .filter((product) => product.platformId === platformId)
                .map((product) => (
                  <option value={product.id} key={product.id}>
                    {product.shortCode} · {product.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="ID do pedido">
            <input
              value={externalId}
              onChange={(event) => setExternalId(event.target.value)}
            />
          </Field>
          <div className="form-row">
            <Field label="Valor da venda">
              <input
                inputMode="decimal"
                value={gross}
                onChange={(event) => setGross(event.target.value)}
              />
            </Field>
            <Field label="Comissão">
              <input
                inputMode="decimal"
                value={commission}
                onChange={(event) => setCommission(event.target.value)}
              />
            </Field>
          </div>
          <div className="form-row">
            <Field label="Moeda">
              <select
                value={currency}
                onChange={(event) =>
                  setCurrency(event.target.value as Currency)
                }
              >
                {(["BRL", "CLP", "USD", "EUR"] as Currency[]).map((item) => (
                  <option value={item} key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as SaleStatus)
                }
              >
                <option value="approved">Aprovada</option>
                <option value="pending">Pendente</option>
                <option value="reversed">Estornada</option>
              </select>
            </Field>
          </div>
        </form>
      </Modal>

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar relatório"
        description="Aceita CSV, TXT ou XLSX da Amazon, Temu e SHEIN."
        footer={
          <>
            <Button variant="secondary" onClick={() => setImportOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmImport} disabled={!report} loading={saving}>
              Importar {report?.sales.length ?? 0} linhas
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Field label="Plataforma">
            <select
              value={platformId}
              onChange={(event) => {
                setPlatformId(event.target.value);
                setReport(null);
                setReportFile(null);
                setReportName("");
              }}
            >
              {data.platforms.map((platform) => (
                <option value={platform.id} key={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>
          </Field>
          <label className="file-drop">
            <FileUp size={28} />
            <strong>{reportName || "Escolher relatório"}</strong>
            <span>CSV, TXT ou XLSX</span>
            <input type="file" accept=".csv,.txt,.xlsx" onChange={fileChange} />
          </label>
          {importError ? (
            <p className="form-error" role="alert">
              {importError}
            </p>
          ) : null}
          {report ? (
            <>
              <div className="import-summary">
                <strong>{report.totalRows} linhas encontradas</strong>
                <span>
                  {report.sales.filter((sale) => sale.productId).length} ligadas
                  a produtos e{" "}
                  {report.sales.filter((sale) => sale.promotionId).length} a
                  links
                </span>
                {report.warnings.map((warning) => (
                  <small key={warning}>{warning}</small>
                ))}
              </div>
              <div className="mapping-grid">
                {(
                  Object.entries(reportFieldLabels) as Array<
                    [ReportField, string]
                  >
                ).map(([field, label]) => (
                  <Field label={label} key={field}>
                    <select
                      value={report.mapping[field] ?? ""}
                      onChange={(event) =>
                        void remap(field, event.target.value)
                      }
                    >
                      <option value="">Não importar</option>
                      {report.headers.map((header) => (
                        <option value={header} key={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </Field>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
